import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { forwardRef, useCallback, useState } from 'react';
import { LoadingSpinner } from './spinner';

const buttonVariants = cva(
  'inline-flex p-0 m-0 items-center justify-center whitespace-nowrap text-sm first-letter:capitalize transition-colors focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-30 antialiased',
  {
    variants: {
      variant: {
        default:
          'h-20 w-72 bg-bg-primary rounded-full py-3 px-4 hover:bg-bg-orange hover:outline hover:outline-4 hover:outline-outline-primary',
        primary:
          'h-12 w-72 flex items-center justify-center py-3 px-2.5 bg-bg-orange-hover border-b-4 border-bd-orange-hover rounded-md font-phosphate text-white text-xl hover:bg-bg-orange hover:border-bd-orange',
        ghost: 'h-6 bg-transparent hover:bg-transparent',
        link: 'text-slate-900 underline-offset-2 hover:underline dark:text-slate-50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, asChild = false, isLoading, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const [isInterallyLoading, setIsInterallyLoading] = useState(false);

    const onClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!props.onClick) {
          return undefined;
        }

        setIsInterallyLoading(true);
        Promise.resolve(props.onClick(event)).finally(() => setIsInterallyLoading(false));
      },
      [props.onClick],
    );

    return (
      <Comp
        className={cn('w-auto', buttonVariants({ variant, className }))}
        ref={ref}
        {...props}
        disabled={props.disabled || isLoading || props.disabled}
        onClick={onClick}
      >
        {isInterallyLoading || isLoading ? <LoadingSpinner /> : props.children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';
